import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { formatRelativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';
import type { SelfRegistration } from '@/types/patients';
import { useSelfRegistrations } from './useSelfRegistrations';
import { sexLabelKey } from './patientDisplay';

interface Props {
  selectedId?: string | undefined;
  onSelect: (submissionId: string) => void;
}

/**
 * The queue of patient-submitted registrations awaiting review.
 *
 * Deliberately shows only what a staff member needs to decide whether to open
 * one — name, sex, phone, how long it has been waiting. The submitted national
 * ID is never in this payload: the list endpoint does not decrypt, so nothing
 * here can leak it.
 */
export function PendingRegistrationsPane({ selectedId, onSelect }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSelfRegistrations('pending');

  if (isLoading) {
    return <p className="py-10 text-center text-[13px] text-muted">{t('common.loading')}</p>;
  }

  if (error) {
    return (
      <p className="py-10 text-center text-[13px] text-destructive">
        {t('selfRegistrations.couldNotLoad')}
      </p>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
          <Icon name="qr" size={22} />
        </div>
        <div className="text-[13.5px] font-semibold tracking-[-0.01em]">
          {t('selfRegistrations.pendingEmpty')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold tracking-[-0.02em]">
          {t('selfRegistrations.pendingTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">{t('selfRegistrations.pendingSubtitle')}</p>
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.id}>
            <RegistrationRow
              item={item}
              selected={item.id === selectedId}
              onSelect={() => onSelect(item.id)}
            />
          </li>
        ))}
      </ul>

      {/* Never let the badge claim a number the list doesn't show. A truncated
          queue that looks complete is how submissions go unreviewed. */}
      {(data?.count ?? 0) > items.length && (
        <p className="text-center text-[12px] text-muted">
          {t('selfRegistrations.showingSomeOf', {
            shown: items.length,
            total: data?.count ?? 0,
          })}
        </p>
      )}
    </div>
  );
}

function RegistrationRow({
  item,
  selected,
  onSelect,
}: {
  item: SelfRegistration;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const meta = [t(sexLabelKey(item.sex)), item.phone].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors',
        selected
          ? 'border-accent bg-paper-2'
          : 'border-line bg-paper hover:bg-paper-2',
      )}
    >
      <InitialsAvatar name={item.fullName} size={36} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em]">
          {item.fullName}
        </div>
        <div className="mt-0.5 truncate text-[12px] text-muted">
          {meta || t('common.unknown')}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[11.5px] text-muted-2">
          {formatRelativeTime(item.submittedAt, t)}
        </div>
        <div className="mt-0.5 text-[12px] font-medium text-accent">
          {t('selfRegistrations.review')}
        </div>
      </div>
    </button>
  );
}
